import React from 'react';
import type { DvcBboxIndex, DvcWeightIndex, OverlayMode, OverlaySelection } from './types';
import { OverlayModeSelector } from './OverlayModeSelector';
import { BatchClassificationBar } from './BatchClassificationBar';
import { SampleImageGrid } from './SampleImageGrid';
import { PaginationControls } from './PaginationControls';
import { DvcClassificationFilterBar } from './DvcClassificationFilterBar';

export const SampleImagesSection: React.FC<{
  /** Full dataset size (before classification filter). */
  totalDatasetImages: number;
  /** URLs shown in the grid after classification filter. */
  gridSampleUrls: string[];
  displayedUrls: string[];
  gridStartIndex: number;
  startIndex: number;
  endIndex: number;
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  useInfiniteScroll: boolean;
  setUseInfiniteScroll: (v: boolean) => void;
  imageContainerRef: React.RefObject<HTMLDivElement | null>;
  overlay: OverlaySelection;
  onToggleOverlay: (mode: OverlayMode) => void;
  hasWeights?: boolean;
  weightIndex: DvcWeightIndex | null;
  bboxIndex: DvcBboxIndex | null;
  classificationIndex: Record<string, string[]> | null;
  selectedUrlsForDisplay: Set<string>;
  onImageSelect: (url: string, globalIndex: number, event: React.MouseEvent) => void;
  onImageClick: (url: string) => void;
  thumbnailDisplayMode: 'direct_s3' | 'presigned';
  apiBaseUrl: string;
  presignedThumbMap: Map<string, string> | null;
  showBatchBar: boolean;
  classificationBatchInput: string;
  onClassificationBatchInputChange: (v: string) => void;
  onAddClassificationsBatch: () => void;
  addingClassificationsBatch: boolean;
  classificationToRemove: string;
  onClassificationToRemoveChange: (v: string) => void;
  selectedImagesClassificationsUnion: string[];
  onRemoveClassificationBatch: () => void;
  removingClassification: boolean;
  onDeselectAll: () => void;
  classificationFilterAvailable: string[];
  classificationFilterSelected: string[];
  onClassificationFilterChange: (next: string[]) => void;
  classificationIndexLoading: boolean;
  classificationIndexError?: string;
  includeLabelsFromCsv: boolean;
  includeLabelsFromDb: boolean;
  onIncludeLabelsFromCsvChange: (checked: boolean) => void;
  onIncludeLabelsFromDbChange: (checked: boolean) => void;
}> = ({
  totalDatasetImages,
  gridSampleUrls,
  displayedUrls,
  gridStartIndex,
  startIndex,
  endIndex,
  totalPages,
  currentPage,
  onPageChange,
  useInfiniteScroll,
  setUseInfiniteScroll,
  imageContainerRef,
  overlay,
  onToggleOverlay,
  hasWeights = false,
  weightIndex,
  bboxIndex,
  classificationIndex,
  selectedUrlsForDisplay,
  onImageSelect,
  onImageClick,
  thumbnailDisplayMode,
  apiBaseUrl,
  presignedThumbMap,
  showBatchBar,
  classificationBatchInput,
  onClassificationBatchInputChange,
  onAddClassificationsBatch,
  addingClassificationsBatch,
  classificationToRemove,
  onClassificationToRemoveChange,
  selectedImagesClassificationsUnion,
  onRemoveClassificationBatch,
  removingClassification,
  onDeselectAll,
  classificationFilterAvailable,
  classificationFilterSelected,
  onClassificationFilterChange,
  classificationIndexLoading,
  classificationIndexError,
  includeLabelsFromCsv,
  includeLabelsFromDb,
  onIncludeLabelsFromCsvChange,
  onIncludeLabelsFromDbChange,
}) => (
  <div className="dvc-sample-data">
    {totalDatasetImages > 0 ? (
      <>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.75rem',
            marginBottom: '1rem',
          }}
        >
          <h3 style={{ margin: 0 }}>
            Images{' '}
            {gridSampleUrls.length !== totalDatasetImages
              ? `(${gridSampleUrls.length} of ${totalDatasetImages})`
              : `(${totalDatasetImages} total)`}
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <OverlayModeSelector
              overlay={overlay}
              onToggle={onToggleOverlay}
              variant="grid"
              hasWeights={hasWeights}
            />
            <label
              style={{ fontSize: '0.9rem', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              <input
                type="checkbox"
                checked={useInfiniteScroll}
                onChange={(e) => setUseInfiniteScroll(e.target.checked)}
                style={{ marginRight: '0.5rem' }}
              />
              Infinite Scroll
            </label>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '0.75rem',
            fontSize: '0.9rem',
          }}
        >
          <span style={{ color: '#555' }}>Classification labels:</span>
          <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={includeLabelsFromCsv}
              onChange={(e) => onIncludeLabelsFromCsvChange(e.target.checked)}
              style={{ marginRight: '0.5rem' }}
            />
            From CSV
          </label>
          <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={includeLabelsFromDb}
              onChange={(e) => onIncludeLabelsFromDbChange(e.target.checked)}
              style={{ marginRight: '0.5rem' }}
            />
            From database
          </label>
          <span style={{ color: '#888', fontSize: '0.85rem' }}>
            Check both for merged (CSV + database).
          </span>
        </div>
        <DvcClassificationFilterBar
          available={classificationFilterAvailable}
          selected={classificationFilterSelected}
          onChange={onClassificationFilterChange}
          loading={classificationIndexLoading}
          error={classificationIndexError}
        />
        {useInfiniteScroll && gridSampleUrls.length > 0 && (
          <div style={{ marginBottom: '1rem', padding: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
            Showing {displayedUrls.length} of {gridSampleUrls.length} images
            {displayedUrls.length < gridSampleUrls.length && ' (scroll to load more)'}
          </div>
        )}
        {showBatchBar && (
          <BatchClassificationBar
            selectedCount={selectedUrlsForDisplay.size}
            classificationBatchInput={classificationBatchInput}
            onBatchInputChange={onClassificationBatchInputChange}
            onAddBatch={onAddClassificationsBatch}
            adding={addingClassificationsBatch}
            classificationToRemove={classificationToRemove}
            onClassificationToRemoveChange={onClassificationToRemoveChange}
            removeOptions={selectedImagesClassificationsUnion}
            onRemoveBatch={onRemoveClassificationBatch}
            removing={removingClassification}
            onDeselectAll={onDeselectAll}
          />
        )}
        {gridSampleUrls.length === 0 ? (
          <div
            style={{
              padding: '2rem',
              textAlign: 'center',
              color: '#666',
              border: '1px dashed #ccc',
              borderRadius: '6px',
            }}
          >
            No images match the selected classification filters. Clear filters above to see all{' '}
            {totalDatasetImages} image(s).
          </div>
        ) : (
          <>
            <div
              ref={imageContainerRef as React.RefObject<HTMLDivElement>}
              className="dvc-sample-images-scroll"
              style={
                useInfiniteScroll
                  ? { maxHeight: '600px', overflowY: 'auto', overflowX: 'hidden' }
                  : undefined
              }
            >
              <SampleImageGrid
                urls={displayedUrls}
                startIndex={gridStartIndex}
                onImageClick={onImageClick}
                onImageSelect={onImageSelect}
                selectedUrls={selectedUrlsForDisplay}
                overlay={overlay}
                weightIndex={weightIndex}
                bboxIndex={bboxIndex}
                classificationIndex={classificationIndex}
                thumbnailDisplayMode={thumbnailDisplayMode}
                apiBaseUrl={apiBaseUrl}
                presignedThumbMap={presignedThumbMap}
              />
            </div>
            {!useInfiniteScroll && totalPages > 1 && (
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                startIndex={startIndex}
                endIndex={endIndex}
                totalItems={gridSampleUrls.length}
                onPageChange={onPageChange}
              />
            )}
          </>
        )}
      </>
    ) : (
      <div className="dvc-no-data">
        <h3>Sample Images</h3>
        <div className="dvc-no-data-message">
          <div>📁</div>
          <p>No sample data available</p>
        </div>
      </div>
    )}
  </div>
);
